/**
 * YMQ 参数手风琴（PDP）
 * 把 YMQ app 渲染的参数输入组（.ymq-options-box）改成单开手风琴：
 * 标题行可点、唯一展开、选完自动前进（答完统一延迟 1.5 秒：窗口内改答案
 * 重置计时、静默期满才前进，手动开合/校验报错即取消）、分支换 box
 * 自动接续、校验报错自动展开，收起面板显示已选值摘要。
 *
 * 依赖线上实测（2026-09-27，threegirls.com）的 YMQ DOM 约定：
 *  - 根容器 <ymq-option ymq-option-block-id>：app block 异步水合后才渲染内容
 *  - 面板 .ymq-options-box：唯一 id="ymq-option-box-{uuid}"；同一选项存在
 *    多个同名分支 box，条件切换是"换 box"，面板身份只能用 id，不能用 label
 *  - 条件显隐：翻转 ymq-option-condition-hide class，DOM 节点不增删
 *  - 选中态：input[ymq-option-checked="true"]（radio/色板；文本类控件无此属性）
 *  - 必填：box[ymq-option-validate="required"]；校验报错：box 内插入 .ymq-option-error
 *
 * 安全边界：
 *  - 状态一律写 data-* 属性（YMQ 观察器只盯其元素的 class 变更并
 *    全量重算，实测一次 260ms+；data 属性不触发）。绝不移动节点
 *  - 绝不写 .ymq-options-box 自身的 display（那是 YMQ 条件显隐的领地）
 *  - 收起过渡：面板由 open 转 collapsed 前先播 ymqAccOut 退出动画
 *    （closeThen/flushExit，box 标 data-acc-exit，CSS 据此替换内容动画），
 *    放完再瞬间切状态——内容已透明，display:none 不产生「闪没」；
 *    触屏/减弱动效直通瞬时。仍只用合成器属性，无逐帧布局
 *  - 上述结构不存在、或可见选项组少于 2 个时全程 no-op，退回 YMQ 平铺渲染
 */
(function () {
  'use strict';

  var SCOPE = '#product-detail-vue';
  var MIN_PANELS = 2;
  var AUTO_ADVANCE = true;
  /* 收起过渡时长：与 CSS ymqAccOut(0.45s) 匹配并留余量，到点由 flushExit
     切状态（抖动修复后窗口期不会被 sync 打断，可安全加长）。仅桌面
     （!COARSE）且非减弱动效时启用 */
  var EXIT_MS = 500;
  /* 面板答完不立即前进：静默满 AUTO_ADVANCE_DELAY 才进入下一个未答
     面板，窗口内改答案即重置计时——给用户留出反悔改选/连续换色看图
     的时间 */
  var AUTO_ADVANCE_DELAY = 1500;
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  /* 触屏主设备（iOS/Android）：WebKit 的元素级 scrollIntoView 会劫持触摸滚动
     （用户实测反馈：手机上选完选项后页面无法滑动），且 iOS 会顺带滚动
     overflow:hidden 的祖先。一律弃用 element.scrollIntoView，改走
     window.scrollTo——只滚文档滚动轴；触屏/减少动效用瞬时定位。 */
  var COARSE = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  /* 各控件类型的内容容器（线上实测）：
     radio/色板/图片选项(4/5/15) = .ymq-option-value-items
     下拉(3)                      = .ymq-option-select-wrapper
     文本/数字(1/2)               = .ymq-option-text-input-wrapper
     文本控件的说明段             = .ymq-option-help-text
     纯文本块(11)                 = .ymq-option-custome-text（无标题，不注册为面板）
     box 内全部命中元素统一标记 data-acc-content（一个面板可有多个内容块，
     收起态由 CSS 按 box 统一隐藏），样式与动画只认该标记 */
  var CONTENT_SEL = '.ymq-option-value-items, .ymq-option-select-wrapper, .ymq-option-text-input-wrapper, .ymq-option-help-text, .ymq-option-custome-text';
  var MONO = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace';
  var CHEVRON = '<svg class="ymq-acc-chevron" width="10" height="7" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

  function scrollBoxIntoView(el, where) {
    var r = el.getBoundingClientRect();
    if (r.top >= 0 && r.bottom <= window.innerHeight) return; // 已在视口内不动
    var top = where === 'center'
      ? r.top + window.scrollY - (window.innerHeight - r.height) / 2
      : r.top + window.scrollY - 8; // nearest：顶部留 8px
    top = Math.max(0, Math.min(top, document.documentElement.scrollHeight - window.innerHeight));
    if (COARSE || REDUCED) window.scrollTo(0, top);
    else window.scrollTo({ top: top, behavior: 'smooth' });
  }

  function initRoot(root) {
    if (root.getAttribute('data-ymq-acc') === '1') return;
    root.setAttribute('data-ymq-acc', '1');
    root.setAttribute('data-acc-init', ''); // 配合 CSS：首次 sync 前不显示平铺闪态

    var ACC = { panels: new Map(), openId: null, userCollapsedAll: false, enabled: false, errBoxId: null, deco: 0, hintBoxId: null, exitTimer: null, exitCommit: null };

    var allBoxes = function () { return Array.prototype.slice.call(root.querySelectorAll('.ymq-options-box')); };
    var visibleBoxes = function () {
      return allBoxes().filter(function (b) { return !b.classList.contains('ymq-option-condition-hide'); });
    };
    var isRequired = function (b) { return b.getAttribute('ymq-option-validate') === 'required'; };
    var labelOf = function (b) { return b.getAttribute('ymq-option-label') || b.id || ''; };

    /* 跨控件类型的"已选值"读取：radio/色板用 ymq-option-checked 属性 + 原生
       checked 兜底（按值去重）；文本/数字取输入值；下拉取选中项文本 */
    function selectedValue(box) {
      var vals = [];
      Array.prototype.forEach.call(box.querySelectorAll('input'), function (i) {
        if ((i.type === 'radio' || i.type === 'checkbox') &&
            (i.getAttribute('ymq-option-checked') === 'true' || i.checked) &&
            i.value && vals.indexOf(i.value) === -1) {
          vals.push(i.value);
        }
      });
      if (vals.length) return vals.join(', ');
      var texts = box.querySelectorAll('input[type="text"], input[type="number"], textarea');
      for (var i = 0; i < texts.length; i++) {
        if (texts[i].value.trim()) return texts[i].value.trim();
      }
      var sels = box.querySelectorAll('select');
      for (var j = 0; j < sels.length; j++) {
        if (sels[j].selectedIndex > 0) return sels[j].options[sels[j].selectedIndex].text;
      }
      return null;
    }
    var isAnswered = function (b) { return selectedValue(b) !== null; };
    var firstUnanswered = function (list) {
      for (var i = 0; i < list.length; i++) {
        if (isRequired(list[i]) && !isAnswered(list[i])) return list[i];
      }
      return list[0] || null;
    };

    /* 注册面板：注入摘要与 chevron。缺 .ymq-option-title（如纯文本块 type 11）
       或内容容器的控件不注册，保持 YMQ 原样平铺。 */
    function teardown() {
      dropExit(); // 清掉在途的收起过渡，避免熔断后 300ms 残留 commit
      cancelAutoAdvance();
      ACC.enabled = false;
      ACC.openId = null;
      ACC.panels.forEach(function (p) {
        p.box.removeAttribute('data-acc-panel');
        p.box.removeAttribute('data-acc-state');
        p.box.removeAttribute('data-acc-done');
        p.box.removeAttribute('data-acc-live');
        var c = p.box.querySelector('[data-acc-content]');
        if (c) c.removeAttribute('data-acc-content');
        var h = p.box.querySelector('[data-acc-header]');
        if (h) {
          h.removeAttribute('data-acc-header');
          h.removeAttribute('role');
          h.removeAttribute('tabindex');
          h.removeAttribute('aria-expanded');
          h.removeAttribute('aria-controls');
          var sm = h.querySelector('.ymq-acc-summary');
          if (sm) sm.remove();
          var cv2 = h.querySelector('.ymq-acc-chevron');
          if (cv2) cv2.remove();
          var hn = h.querySelector('.ymq-acc-hint');
          if (hn) hn.remove();
        }
      });
      ACC.panels.clear();
      root.removeAttribute('data-acc-on');
      root.setAttribute('data-acc-broken', '');
    }

    function ensurePanel(b) {
      var title = b.querySelector('.ymq-option-title');
      var vis = b.querySelectorAll(CONTENT_SEL);
      if (!title || !vis.length) return null;
      var p = ACC.panels.get(b.id);
      var vi = vis[0];
      if (!p) {
        p = { box: b, header: title, vi: vi, hidden: b.classList.contains('ymq-option-condition-hide'), open: undefined, wasAnswered: undefined };
        ACC.panels.set(b.id, p);
        b.setAttribute('data-acc-panel', '');
        /* 标题装饰与事件监听只在首次注册时执行（重复执行会叠加监听器、
           重复注入摘要/箭头）；内容标记在下方每轮幂等重刷 */
        /* 熔断器：YMQ 在异常水合状态下可能反复重建 box（实测一轮失控重建
           产生数千个重复注入元素并叠加监听）。装饰总量超过 box 总数 3 倍
           即判定渲染抖动，整体退化为平铺并清理痕迹。 */
        ACC.deco++;
        if (ACC.deco > root.querySelectorAll('.ymq-options-box').length * 3 + 10) {
          teardown();
          return null;
        }
        title.setAttribute('data-acc-header', '');
        title.setAttribute('role', 'button');
        title.setAttribute('tabindex', '0');
        title.setAttribute('aria-expanded', 'false');
        if (!vi.id) vi.id = 'ymq-acc-c-' + b.id;
        title.setAttribute('aria-controls', vi.id);
        // 标题原始文本须在注入摘要/箭头之前抓取，供分支换 box 的同名接续匹配
        p.label = (title.textContent || '').trim();
        title.insertAdjacentHTML('beforeend', '<span class="ymq-acc-summary"></span>' + CHEVRON);
        p.summary = title.querySelector('.ymq-acc-summary');
        title.addEventListener('click', function (e) {
          if (!ACC.enabled) return;
          if (e.target.closest('a, button, .option-help')) return; // 不劫持帮助图标等
          // 开合是纯 UI 行为，阻断冒泡——YMQ 根级点击处理每次会全量重算
          // （真机实测数百毫秒、期间 0 帧），是手风琴点击卡顿主因
          e.stopPropagation();
          toggle(b.id);
        });
        title.addEventListener('keydown', function (e) {
          if (!ACC.enabled) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(b.id); }
        });
      }
      // 幂等重刷：YMQ 可能在注册后插入新的内容节点（如说明段）
      Array.prototype.forEach.call(vis, function (v) { v.setAttribute('data-acc-content', ''); });
      return p;
    }

    /* 开合：高度瞬时切换，内容以合成器属性（opacity/transform）过渡。
     * 不做 max-height 逐帧动画——真实 PDP 单次全列布局实测 150~500ms，
     * 逐帧动画只能跑出 2~3 帧、且把主线程阻塞近 1 秒（点击卡顿本体）。
     * 所有 DOM 写入保持幂等：值未变化的写入仍会产生 mutation 记录，
     * 被自己的观察器捕获后会形成 sync↔写入 的无限循环。 */
    function applyOpenState(id) {
      ACC.panels.forEach(function (p) {
        var open = ACC.enabled && p.box.id === id && !p.hidden;
        p.open = open;
        var st = ACC.enabled && !p.hidden ? (open ? 'open' : 'collapsed') : null;
        if (p.box.getAttribute('data-acc-state') !== st) {
          if (st === null) p.box.removeAttribute('data-acc-state');
          else p.box.setAttribute('data-acc-state', st);
        }
        var ex = String(open);
        if (p.header.getAttribute('aria-expanded') !== ex) p.header.setAttribute('aria-expanded', ex);
        if ((!ACC.enabled || p.hidden) && p.vi.style.maxHeight) p.vi.style.maxHeight = '';
      });
    }

    function openPanel(id, opts) {
      opts = opts || {};
      ACC.openId = id;
      ACC.userCollapsedAll = false;
      applyOpenState(id);
      var b = document.getElementById(id);
      if (b && opts.scroll) {
        // 等一帧让 display 切换先行布局，再按新几何定位
        requestAnimationFrame(function () { scrollBoxIntoView(b, 'nearest'); });
      }
    }

    /* 收起过渡时序：会让某个 open 面板转为 collapsed 的状态变更先包进
     * closeThen——给该面板标 data-acc-exit 播退出动画（空间暂留），EXIT_MS
     * 后由 flushExit 清标记并执行 commit（瞬间切状态 + 展开下一项）。
     * 只有用户主动开合（toggle）会提前 flush 结算在途过渡；sync 在过渡
     * 在途时直接跳过前进判定（YMQ 选择后的 class/条件显隐连锁变更会触发
     * 数轮 sync，提前结算会把退出动画拦腰截断——抖动主因），目标面板
     * 失效由 flushExit 的补偿 sync 恢复。触屏/减弱动效/无可见内容块/
     * 已禁用时直通，不做过渡。 */
    function clearExitMark() {
      Array.prototype.forEach.call(root.querySelectorAll('[data-acc-exit]'), function (b) {
        b.removeAttribute('data-acc-exit');
      });
    }

    function flushExit() {
      if (!ACC.exitTimer) return;
      clearTimeout(ACC.exitTimer);
      ACC.exitTimer = null;
      var commit = ACC.exitCommit;
      ACC.exitCommit = null;
      // 先摘标记再切状态：标记残留会让面板下次展开时误播退出动画
      clearExitMark();
      if (commit && ACC.enabled) commit();
      // commit 落地后若意图展开的面板已失效（分支条件翻转/被移除），补一轮 sync 恢复开合
      var ob = ACC.openId && document.getElementById(ACC.openId);
      if (ACC.enabled && ACC.openId && !(ob && !ob.classList.contains('ymq-option-condition-hide'))) scheduleSync();
    }

    /* 丢弃未结算的收起过渡（不执行 commit）：报错联动等更高优先级的状态
       变更接管面板开合、或用户主动收起当前面板时使用，避免旧 commit
       延迟/反向覆盖 */
    function dropExit() {
      if (!ACC.exitTimer) return;
      clearTimeout(ACC.exitTimer);
      ACC.exitTimer = null;
      ACC.exitCommit = null;
      clearExitMark();
    }

    function closeThen(commit) {
      flushExit();
      var b = ACC.openId && document.getElementById(ACC.openId);
      var exitable = b && b.getAttribute('data-acc-state') === 'open'
        && !b.classList.contains('ymq-option-condition-hide') // 已被条件隐藏的面板退出动画不可见，直通
        && b.querySelector('[data-acc-content]');
      if (!exitable || COARSE || REDUCED || !ACC.enabled) { commit(); return; }
      b.setAttribute('data-acc-exit', '');
      ACC.exitCommit = commit;
      ACC.exitTimer = setTimeout(flushExit, EXIT_MS);
    }

    function toggle(id) {
      if (ACC.openId === id) {
        /* 用户主动收起：丢弃在途过渡立即落地（内容多在半透明区，截断
           几乎不可见），不再等 EXIT_MS */
        dropExit();
        cancelAutoAdvance(); // 收起当前面板 = 手动接管，待发的自动前进作废
        ACC.openId = null;
        ACC.userCollapsedAll = true;
        applyOpenState(null);
        return;
      }
      /* 顺序门禁：上方存在未填的必填项时不展开，弹提示并高亮首个未填项
         （收起不受限；已答面板回看在其上方无未答必填项时不受限） */
      var blocker = null;
      var boxes = visibleBoxes().filter(function (b) { return ACC.panels.has(b.id); });
      for (var i = 0; i < boxes.length; i++) {
        if (boxes[i].id === id) break;
        if (isRequired(boxes[i]) && !isAnswered(boxes[i])) { blocker = boxes[i]; break; }
      }
      if (blocker) {
        // 被门禁拦截的点击没有真正展开，不影响待发的自动前进
        promptBlocker(blocker);
        return;
      }
      cancelAutoAdvance(); // 真正手动展开其他面板 = 手动接管
      closeThen(function () { openPanel(id); });
    }

    /* 顺序提示：默认不显示；仅在乱序点击被拦截时出现在被拦的首个未填
       必填面板上（淡红文字），保持到该项填完才消失。此处记录被拦面板
       并做注意力反馈：标题脉冲 + 滚动定位 */
    function promptBlocker(blocker) {
      ACC.hintBoxId = blocker.id;
      maintainHints(); // 立即渲染：data-acc-nudge 不在观察器过滤内，不能等下一轮 sync
      blocker.removeAttribute('data-acc-nudge');
      void blocker.offsetWidth; // 重启动画
      blocker.setAttribute('data-acc-nudge', '');
      setTimeout(function () { blocker.removeAttribute('data-acc-nudge'); }, 1600);
      var r = blocker.getBoundingClientRect();
      if (r.top < 0 || r.bottom > window.innerHeight) {
        scrollBoxIntoView(blocker, 'center');
      }
    }

    /* 顺序提示维护：只在乱序被拦后显示（hintBoxId 记录被拦面板），
       该项填完或失效（隐藏/移除）即清除。幂等（未变化不动 DOM） */
    function maintainHints() {
      if (ACC.hintBoxId) {
        var hp = ACC.panels.get(ACC.hintBoxId);
        if (!hp || hp.hidden || !hp.box.isConnected || isAnswered(hp.box)) {
          ACC.hintBoxId = null;
        }
      }
      ACC.panels.forEach(function (p) {
        if (!p.header) return;
        var existing = p.header.querySelector('.ymq-acc-hint');
        var should = !!(ACC.hintBoxId && p.box.id === ACC.hintBoxId);
        if (should && !existing) {
          var hint = document.createElement('span');
          hint.className = 'ymq-acc-hint';
          hint.setAttribute('role', 'status');
          hint.textContent = 'Please complete this first';
          p.header.appendChild(hint);
        } else if (!should && existing) {
          existing.remove();
        }
      });
    }

    /* 步进灰显：面板"已到这一步"（live）= 已填过，或上方没有未填的必填项
       （与 toggle 的开合门禁同一判定）。非 live 面板标题灰显，到达/填过
       后恢复深色。只写 data 属性，幂等（未变化不动 DOM） */
    function maintainLive(vis) {
      var blocked = false;
      vis.forEach(function (b) {
        var live = !blocked || isAnswered(b);
        var has = b.hasAttribute('data-acc-live');
        if (live && !has) b.setAttribute('data-acc-live', '');
        else if (!live && has) b.removeAttribute('data-acc-live');
        if (isRequired(b) && !isAnswered(b)) blocked = true;
      });
    }

    function updateSummaries() {
      ACC.panels.forEach(function (p) {
        var s = p.summary;
        if (!s) return;
        var next = selectedValue(p.box) || '';
        if (s.textContent !== next) s.textContent = next; // 幂等：未变化不写，避免观察器自触发
        if (next) { if (!p.box.hasAttribute('data-acc-done')) p.box.setAttribute('data-acc-done', ''); }
        else if (p.box.hasAttribute('data-acc-done')) p.box.removeAttribute('data-acc-done');
      });
    }

    var syncTimer = null;
    function scheduleSync() {
      if (syncTimer) return;
      syncTimer = setTimeout(function () { syncTimer = null; sync(); }, 60);
    }

    /* 延迟自动前进（所有面板）：答完先保持当前面板展开，静默满
       AUTO_ADVANCE_DELAY 才进入下一个未答面板；窗口内改答案重置计时
       （radio/色板走观察器、下拉/文本走 change/input 联动 sync），
       手动开合 / 校验报错 / 熔断整体取消。目标面板不预先固定——窗口内
       分支与答案都可能变化，到点按当下状态重算。 */
    var advTimer = null;
    var advPend = null; // { srcId, srcSig }

    function cancelAutoAdvance() {
      if (advTimer) clearTimeout(advTimer);
      advTimer = null;
      advPend = null;
    }

    function armAutoAdvance(srcBox) {
      cancelAutoAdvance();
      advPend = { srcId: srcBox.id, srcSig: selectedValue(srcBox) };
      advTimer = setTimeout(function () {
        advTimer = null;
        var pend = advPend;
        advPend = null;
        fireAutoAdvance(pend);
      }, AUTO_ADVANCE_DELAY);
    }

    function fireAutoAdvance(pend) {
      if (!pend || !ACC.enabled) return;
      var src = document.getElementById(pend.srcId);
      if (!src || !src.isConnected || !isAnswered(src)) return;
      if (ACC.openId !== src.id) return; // 已被手动接管
      var vis = visibleBoxes().filter(function (b) { return ACC.panels.has(b.id); });
      var idx = vis.indexOf(src);
      if (idx === -1) return;
      for (var k = idx + 1; k < vis.length; k++) {
        // 与即时前进同款：closeThen 先播当前面板收起过渡再切换（手机直通）
        if (!isAnswered(vis[k])) { closeThen(function () { openPanel(vis[k].id); }); return; }
      }
    }

    /* 每轮 sync 在自动前进分支之前维护待发状态：
       源面板被换 box（同名分支）→ 迁移到新 box 并重置计时；
       openId 已不是源面板 → 手动接管，取消；
       选中值变化 → 用户在窗口内改了答案，重置计时 */
    function maintainPendingAdvance() {
      if (!advPend) return;
      if (!ACC.enabled) { cancelAutoAdvance(); return; }
      var src = document.getElementById(advPend.srcId);
      if (!src || !src.isConnected) { cancelAutoAdvance(); return; }
      if (src.classList.contains('ymq-option-condition-hide')) {
        var sp = ACC.panels.get(src.id);
        var vis = visibleBoxes();
        var nb = null;
        for (var i = 0; i < vis.length; i++) {
          var vp = ACC.panels.get(vis[i].id);
          if (sp && vp && vp !== sp && vp.label === sp.label && isAnswered(vis[i])) { nb = vis[i]; break; }
        }
        if (nb) {
          ACC.openId = nb.id;
          applyOpenState(nb.id);
          armAutoAdvance(nb);
        } else {
          cancelAutoAdvance(); // 无同名接续 box：交回下方结构修复分支处理
        }
        return;
      }
      if (ACC.openId !== advPend.srcId) { cancelAutoAdvance(); return; }
      if (selectedValue(src) !== advPend.srcSig) armAutoAdvance(src);
    }

    function sync() {
      if (root.hasAttribute('data-acc-broken')) return; // 熔断后本会话保持平铺
      // 清掉被移出 DOM 的面板（如 YMQ 重渲染）
      Array.prototype.slice.call(ACC.panels.keys()).forEach(function (id) {
        var p = ACC.panels.get(id);
        if (!p.box.isConnected) {
          ACC.panels.delete(id);
          if (ACC.openId === id) ACC.openId = null;
        }
      });

      var allVis = visibleBoxes();
      var newly = [];
      allVis.forEach(function (b) {
        var p = ensurePanel(b);
        if (p && p.hidden) { p.hidden = false; newly.push(b); }
      });
      ACC.panels.forEach(function (p) {
        p.hidden = p.box.classList.contains('ymq-option-condition-hide');
      });
      // 只统计已注册且当前可见的面板（缺标题/内容结构的控件不参与手风琴）
      var vis = allVis.filter(function (b) { return ACC.panels.has(b.id); });

      // 可见面板数不足时整体退回 YMQ 平铺
      var shouldEnable = vis.length >= MIN_PANELS;
      if (shouldEnable !== ACC.enabled) {
        cancelAutoAdvance();
        ACC.enabled = shouldEnable;
        if (shouldEnable) root.setAttribute('data-acc-on', '');
        else root.removeAttribute('data-acc-on');
        ACC.openId = null;
        ACC.userCollapsedAll = false;
        ACC.panels.forEach(function (p) { p.open = undefined; });
        ACC.panels.forEach(function (p) {
          p.box.removeAttribute('data-acc-state');
          p.box.removeAttribute('data-acc-live');
          var vi = p.box.querySelector('[data-acc-content]');
          if (vi) vi.style.maxHeight = '';
        });
      }

      if (!root.hasAttribute('data-acc-ready')) root.setAttribute('data-acc-ready', '');
      if (!ACC.enabled) { applyOpenState(null); updateSummaries(); return; }

      // 待发延迟前进的维护须先于分支链（换 box 迁移会改写 openId）
      maintainPendingAdvance();

      var openBox = ACC.openId && document.getElementById(ACC.openId);
      var openVisible = openBox && !openBox.classList.contains('ymq-option-condition-hide');
      var openP = openBox && ACC.panels.get(openBox.id);
      /* 自动前进/分支揭示只在「未答→已答」的首次完成瞬间触发：
         回看或修改已答面板时不再被拉走，看完自行打开下一个 */
      var justAnswered = !!(AUTO_ADVANCE && openP && openVisible && openP.wasAnswered === false && isAnswered(openBox));

      /* 收起过渡在途时跳过前进判定（意图已由 pending commit 表达；YMQ 选择后
         的 class/条件显隐连锁变更会触发数轮 sync，若在此提前结算会把退出
         动画拦腰截断——上一版抖动主因）。报错联动仍在下方处理，目标面板
         失效由 flushExit 的补偿 sync 恢复 */
      if (!ACC.exitTimer) {
        if (openBox && !openVisible) {
          // 当前展开面板被条件隐藏 → 打开第一个未答面板（换 box / 分支收起）
          // 当前面板已随 box 整体隐藏，退出动画不可见，closeThen 会直通
          var t1 = firstUnanswered(vis);
          if (t1) closeThen(function () { openPanel(t1.id, { scroll: true }); });
        } else if (newly.length && AUTO_ADVANCE && !openBox) {
          // 分支在无展开面板时揭示了新面板（如全部收起后条件翻转）：
          // 没有面板在等延迟窗口，直接进入
          var t2 = null;
          for (var i = 0; i < newly.length; i++) {
            if (isRequired(newly[i]) && !isAnswered(newly[i])) { t2 = newly[i]; break; }
          }
          closeThen(function () { openPanel((t2 || newly[0]).id, { scroll: true }); });
        } else if (justAnswered) {
          // 面板答完：不立即跳走，静默满 AUTO_ADVANCE_DELAY 才前进；
          // 窗口内改答案由 maintainPendingAdvance 重置计时
          armAutoAdvance(openBox);
        } else if (!ACC.openId && !ACC.userCollapsedAll && vis.length) {
          // 初始：展开第一个未答必填面板
          var t3 = firstUnanswered(vis);
          if (t3) openPanel(t3.id);
        }
      }

      applyOpenState(ACC.openId);
      maintainLive(vis);
      updateSummaries();

      /* 乱序完成检测：刚答完的面板上方仍存在未填必填项时，
         点亮首个未填项的提示（用户绕过开合门禁完成后续选项的场景） */
      if (justAnswered) {
        for (var qi = 0; qi < vis.length; qi++) {
          if (vis[qi].id === openBox.id) break;
          if (isRequired(vis[qi]) && !isAnswered(vis[qi])) { ACC.hintBoxId = vis[qi].id; break; }
        }
      }

      /* 校验报错联动：真实 YMQ 的 .ymq-option-error 是预渲染的空元素，
         校验时填入文字（childList 变更，会被观察器捕捉 → sync）。
         取可见面板里第一个有报错文字的 box：收起则展开+滚动，首个变化时闪烁。 */
      var errBoxId = null;
      for (var q = 0; q < vis.length; q++) {
        var errEl = vis[q].querySelector('.ymq-option-error');
        if (errEl && (errEl.textContent || '').trim()) { errBoxId = vis[q].id; break; }
      }
      if (errBoxId) {
        cancelAutoAdvance(); // 校验报错联动优先，待发的自动前进作废
        var eb = document.getElementById(errBoxId);
        /* 报错优先于收起过渡：报错面板正在退出→停下保持展开亮错；
           报错面板收起→丢弃过渡立即展开（不等 300ms，旧 commit 也别再接管） */
        if (ACC.exitTimer && ACC.openId === errBoxId) dropExit();
        if (eb.getAttribute('data-acc-state') === 'collapsed') { dropExit(); openPanel(errBoxId, { scroll: true }); }
        if (ACC.errBoxId !== errBoxId) {
          ACC.errBoxId = errBoxId;
          eb.removeAttribute('data-acc-flash');
          void eb.offsetWidth; // 重启动画
          eb.setAttribute('data-acc-flash', '');
        }
      } else {
        ACC.errBoxId = null;
      }

      maintainHints();

      // 记录本轮答案快照，供下一轮判定「未答→已答」转移
      ACC.panels.forEach(function (p) {
        p.wasAnswered = isAnswered(p.box);
      });
    }

    /* 观察器：class 翻转（条件显隐）、ymq-option-checked（选择）、
       childList（水合 / 校验报错填充文字）。所有变化统一走防抖后的 sync，
       报错联动在 sync 内完成。 */
    var obs = new MutationObserver(function () {
      scheduleSync();
    });
    obs.observe(root, { childList: true, subtree: true, attributeFilter: ['class', 'ymq-option-checked'] });

    // 文本输入：实时更新摘要；change（失焦/回车）时才参与自动前进。
    // 延迟窗口内继续改文本：值变更不产生 class/checked mutation，须主动
    // sync 让 maintainPendingAdvance 重置计时（radio/色板/下拉无此需求）
    root.addEventListener('input', function (e) {
      if (!e.target.matches('input, textarea, select')) return;
      updateSummaries();
      if (advPend) scheduleSync();
    });
    root.addEventListener('change', function (e) {
      if (e.target.matches('input, select, textarea')) scheduleSync();
    });

    scheduleSync();
  }

  function scan(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('ymq-option'), initRoot);
  }

  // ymq-option 根元素本身也是异步出现的（app 水合后才插入）
  var rootObs = new MutationObserver(function (muts) {
    muts.forEach(function (m) {
      Array.prototype.forEach.call(m.addedNodes, function (n) {
        if (n.nodeType !== 1) return;
        if (n.tagName === 'YMQ-OPTION') initRoot(n);
        else if (n.querySelectorAll) {
          Array.prototype.forEach.call(n.querySelectorAll('ymq-option'), initRoot);
        }
      });
    });
  });
  function watchRoots(scope) {
    rootObs.observe(scope, { childList: true, subtree: true });
  }

  function boot() {
    var scope = document.querySelector(SCOPE);
    if (!scope) return;
    scan(scope);
    watchRoots(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
